/**
 * GoalMint design system — Midnight Wealth (light) + dark mode palettes.
 * "Mint" in the wordmark stays blue (`brand.accent`). Total Saved/Paid card stays blue.
 */

import { applyAccentColor, type AccentColorId } from './accentColors';

/** Unified header / nav chrome — dark blue-green slate (sampled from design reference). */
export const HEADER_BACKGROUND = '#213237';

/** Full-screen boot and loading states — very dark green. */
export const LOADING_BACKGROUND = '#1A2B2F';

export type ThemeMode = 'light' | 'dark';

export type AppColors = {
  brand: { accent: string };
  accent: { mint: string; mintDark: string };
  nav: {
    background: string;
    border: string;
    activeBackground: string;
    activeText: string;
    text: string;
    textMuted: string;
  };
  primary: {
    main: string;
    light: string;
    header: string;
    dark: string;
    background: string;
  };
  secondary: {
    main: string;
    light: string;
    dark: string;
    background: string;
  };
  success: { main: string; light: string; dark: string; background: string };
  warning: { main: string; light: string; dark: string; background: string };
  error: { main: string; light: string; dark: string; background: string };
  info: { main: string; light: string; dark: string; background: string };
  grey: {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
    cancel: string;
  };
  text: {
    primary: string;
    secondary: string;
    disabled: string;
    hint: string;
    placeholder: string;
    onNav: string;
    onDark: string;
  };
  background: {
    default: string;
    paper: string;
    goals: string;
    income: string;
    timeline: string;
    progress: string;
  };
  border: string;
  surface: string;
  /** Card outline — transparent in light, subtle white in dark. */
  cardEdge: string;
  dashboard: {
    incomeCard: string;
    savedPaidCard: string;
    savingsProgress: string;
    debtProgress: string;
    incomeCardBorderDark: string;
  };
  auth: {
    panel: string;
    cta: string;
    ctaText: string;
  };
  loading: {
    background: string;
  };
  tabBar: {
    background: string;
    active: string;
    inactive: string;
  };
  button: {
    primary: string;
    primaryText: string;
  };
  goalType: {
    savings: string;
    debt: string;
    investment: string;
  };
};

export const lightPalette: AppColors = {
  brand: { accent: '#42A5F5' },
  accent: { mint: '#34D399', mintDark: '#10B981' },
  nav: {
    background: HEADER_BACKGROUND,
    border: 'rgba(255,255,255,0.1)',
    activeBackground: '#3D454C',
    activeText: '#34D399',
    text: 'rgba(255,255,255,0.92)',
    textMuted: 'rgba(255,255,255,0.75)',
  },
  primary: {
    main: '#166534',
    light: '#15803D',
    header: HEADER_BACKGROUND,
    dark: '#1A2B2F',
    background: '#F1F5F9',
  },
  secondary: {
    main: '#2563EB',
    light: '#42A5F5',
    dark: '#1D4ED8',
    background: '#DBEAFE',
  },
  success: {
    main: '#10B981',
    light: '#34D399',
    dark: '#059669',
    background: '#D1FAE5',
  },
  warning: {
    main: '#F59E0B',
    light: '#FBBF24',
    dark: '#D97706',
    background: '#FEF3C7',
  },
  error: {
    main: '#EF4444',
    light: '#F87171',
    dark: '#DC2626',
    background: '#FEE2E2',
  },
  info: {
    main: '#0288D1',
    light: '#B3E5FC',
    dark: '#01579B',
    background: '#E0F2FE',
  },
  grey: {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#334155',
    800: '#1E293B',
    900: '#0F172A',
    cancel: '#94A3B8',
  },
  text: {
    primary: '#0F172A',
    secondary: '#64748B',
    disabled: '#94A3B8',
    hint: '#94A3B8',
    placeholder: '#94A3B8',
    onNav: '#FFFFFF',
    onDark: '#FFFFFF',
  },
  background: {
    default: '#FFFFFF',
    paper: '#F1F5F9',
    goals: '#F1F5F9',
    income: '#F1F5F9',
    timeline: '#F1F5F9',
    progress: '#F1F5F9',
  },
  border: '#E2E8F0',
  surface: '#FFFFFF',
  cardEdge: 'transparent',
  dashboard: {
    incomeCard: HEADER_BACKGROUND,
    savedPaidCard: '#2563EB',
    savingsProgress: '#22C55E',
    debtProgress: '#2563EB',
    incomeCardBorderDark: '#34D399',
  },
  auth: {
    panel: HEADER_BACKGROUND,
    cta: '#166534',
    ctaText: '#FFFFFF',
  },
  loading: {
    background: LOADING_BACKGROUND,
  },
  button: {
    primary: '#166534',
    primaryText: '#FFFFFF',
  },
  goalType: {
    savings: '#22C55E',
    debt: '#2563EB',
    investment: '#0F172A',
  },
  tabBar: {
    background: '#FFFFFF',
    active: HEADER_BACKGROUND,
    inactive: '#94A3B8',
  },
};

export const darkPalette: AppColors = {
  ...lightPalette,
  nav: {
    background: HEADER_BACKGROUND,
    border: 'rgba(255,255,255,0.08)',
    activeBackground: '#3D454C',
    activeText: '#34D399',
    text: 'rgba(255,255,255,0.92)',
    textMuted: 'rgba(255,255,255,0.7)',
  },
  primary: {
    main: '#15803D',
    light: '#166534',
    header: HEADER_BACKGROUND,
    dark: '#1A2B2F',
    background: '#0F172A',
  },
  text: {
    primary: '#F8FAFC',
    secondary: '#94A3B8',
    disabled: '#64748B',
    hint: '#64748B',
    placeholder: '#64748B',
    onNav: '#FFFFFF',
    onDark: '#FFFFFF',
  },
  background: {
    default: '#0F172A',
    paper: '#1E293B',
    goals: '#1E293B',
    income: '#1E293B',
    timeline: '#1E293B',
    progress: '#1E293B',
  },
  border: '#334155',
  surface: '#1E293B',
  cardEdge: 'rgba(255,255,255,0.12)',
  grey: {
    50: '#1E293B',
    100: '#334155',
    200: '#475569',
    300: '#64748B',
    400: '#94A3B8',
    500: '#CBD5E1',
    600: '#E2E8F0',
    700: '#F1F5F9',
    800: '#F8FAFC',
    900: '#FFFFFF',
    cancel: '#64748B',
  },
  tabBar: {
    background: '#0F172A',
    active: HEADER_BACKGROUND,
    inactive: '#64748B',
  },
  dashboard: {
    incomeCard: HEADER_BACKGROUND,
    savedPaidCard: '#2563EB',
    savingsProgress: '#22C55E',
    debtProgress: '#3B82F6',
    incomeCardBorderDark: '#34D399',
  },
  auth: {
    panel: HEADER_BACKGROUND,
    cta: '#15803D',
    ctaText: '#FFFFFF',
  },
  button: {
    primary: '#15803D',
    primaryText: '#FFFFFF',
  },
  goalType: {
    savings: '#22C55E',
    debt: '#3B82F6',
    investment: '#94A3B8',
  },
  secondary: {
    main: '#3B82F6',
    light: '#60A5FA',
    dark: '#2563EB',
    background: '#1E3A5F',
  },
  success: {
    main: '#34D399',
    light: '#6EE7B7',
    dark: '#10B981',
    background: '#064E3B',
  },
  warning: {
    main: '#FBBF24',
    light: '#FCD34D',
    dark: '#F59E0B',
    background: '#451A03',
  },
  error: {
    main: '#F87171',
    light: '#FCA5A5',
    dark: '#EF4444',
    background: '#450A0A',
  },
  info: {
    main: '#38BDF8',
    light: '#7DD3FC',
    dark: '#0EA5E9',
    background: '#0C4A6E',
  },
  loading: {
    background: LOADING_BACKGROUND,
  },
};

export function resolveThemeMode(mode: ThemeMode): 'light' | 'dark' {
  return mode;
}

export function getPalette(resolved: 'light' | 'dark'): AppColors {
  return resolved === 'dark' ? darkPalette : lightPalette;
}

/** Base palette plus optional accent color overlay. Default accent leaves colors unchanged. */
export function getThemedPalette(
  resolved: 'light' | 'dark',
  accentId: AccentColorId = 'default'
): AppColors {
  const base = getPalette(resolved);
  if (accentId === 'default') {
    return base;
  }
  return applyAccentColor(
    JSON.parse(JSON.stringify(base)) as AppColors,
    accentId,
    resolved
  );
}
